"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion } from "framer-motion";
import { MapPin, Calendar, ArrowRight, CircleCheck as CheckCircle2, TrendingUp, ShieldCheck, Star, Award, Sparkles } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const trustHighlights = [{
  id: 1,
  title: "100% Direct Access",
  desc: "Work directly with Yassine Essaadi"
}, {
  id: 2,
  title: "Google Map Pack",
  desc: "Local visibility & citations"
}, {
  id: 3,
  title: "Tailored Strategy",
  desc: "Custom roadmap for Belfast businesses"
}];
const keyBenefits = [{
  id: 1,
  text: "Direct consultant collaboration — no junior account managers"
}, {
  id: 2,
  text: "Data-driven local keyword targeting for Belfast & NI"
}, {
  id: 3,
  text: "Google Business Profile & local citation optimization"
}, {
  id: 4,
  text: "Transparent ROI reporting & clear actionable deliverables"
}];
export default function Hero() {
  return <section id="herolocalauthority" data-nav="dark" className="relative min-h-[85vh] w-full bg-primary text-primary-foreground py-16 lg:py-24 overflow-hidden flex items-center">
      {}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-accent/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-10 w-80 h-80 bg-accent/15 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col items-start text-left space-y-6">
            {}
            <Badge variant="outline" className="bg-accent/20 text-primary-foreground border-accent/40 px-3.5 py-1.5 text-xs md:text-sm font-medium tracking-wide rounded-full flex items-center gap-2 backdrop-blur-xs">
              <MapPin className="w-4 h-4 text-accent shrink-0" />
              <span>Belfast &amp; Northern Ireland Local SEO Expert</span>
            </Badge>

            {}
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
              Local SEO Consultant in Belfast
            </h2>

            {}
            <p className="font-sans text-base sm:text-lg md:text-xl text-primary-foreground/90 max-w-2xl leading-relaxed">
              I help Belfast businesses capture local search traffic, dominate Google Maps, and secure sustainable organic growth. When you work with me, you get my direct personal expertise — no agencies, no middlemen, just tailored strategy.
            </p>

            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-2xl pt-2">
              {keyBenefits.map((item, i) => <div key={item.id} data-index={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                  <span className="font-sans text-xs sm:text-sm text-primary-foreground/90">
                    {item.text}
                  </span>
                </div>)}
            </div>

            {}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto pt-4">
              <Link to="/ContactPage">
                <Button size="lg" className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-7 py-6 text-base rounded-md shadow-lg flex items-center justify-center gap-2 transition-[background-color,box-shadow]">
                  <SafeHydrate><Calendar className="w-5 h-5" /></SafeHydrate>
                  <span>Book a Free SEO Consultation</span>
                </Button>
              </Link>

              <Link to="/CaseStudiesPage">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-primary-foreground/30 text-white hover:bg-primary-foreground/10 font-semibold px-7 py-6 text-base rounded-md flex items-center justify-center gap-2 transition-[background-color,border-color]">
                  <span>Explore My Case Studies</span>
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>

            {}
            <div className="pt-4 flex items-center gap-3 text-xs text-primary-foreground/80">
              <ShieldCheck className="w-4 h-4 text-accent" />
              <span>Transparent deliverables, no long-term lock-in contracts</span>
            </div>
          </motion.div>

          {}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2,
          ease: "easeOut"
        }} className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none bg-card text-card-foreground rounded-xl p-6 sm:p-8 shadow-2xl border border-border">
              
              {}
              <div className="flex items-center gap-4 mb-6">
                <div className="relative w-20 h-20 rounded-full overflow-hidden border-2 border-accent shrink-0 shadow-md">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Independent Freelance SEO Consultant" loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div>
                  <h2 className="font-serif text-xl font-bold text-foreground">
                    Yassine Essaadi
                  </h2>
                  <p className="font-sans text-sm font-medium text-accent">
                    Freelance Local SEO Consultant
                  </p>
                  <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                    <Award className="w-3.5 h-3.5 text-accent" />
                    <span>Independent SEO Strategist</span>
                  </div>
                </div>
              </div>

              <Separator className="my-4" />

              {}
              <div className="bg-muted p-4 rounded-lg border border-border mb-6">
                <p className="font-sans text-xs sm:text-sm text-foreground/90 italic leading-relaxed">
                  &quot;I work directly with Belfast business owners to build tailored SEO strategies that deliver qualified leads and long-term search visibility.&quot;
                </p>
              </div>

              {}
              <div className="space-y-3">
                {trustHighlights.map((item, i) => <div key={item.id} data-index={i} className="flex items-center justify-between p-3 rounded-lg bg-background border border-border">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-md bg-accent/10 text-accent">
                        {item.id === 1 && <Star className="w-4 h-4" />}
                        {item.id === 2 && <MapPin className="w-4 h-4" />}
                        {item.id === 3 && <TrendingUp className="w-4 h-4" />}
                      </div>
                      <div>
                        <div className="font-serif text-xs font-semibold text-foreground">
                          {item.title}
                        </div>
                        <div className="font-sans text-2xs text-muted-foreground">
                          {item.desc}
                        </div>
                      </div>
                    </div>
                    <Sparkles className="w-4 h-4 text-accent/60" />
                  </div>)}
              </div>

              {}
              <div className="mt-6 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span>Available for Belfast Consultations</span>
                </span>
                <Link to="/AboutMePage" className="text-accent hover:underline font-medium">
                  Learn About Me &rarr;
                </Link>
              </div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}